import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AddressInput from '@/components/forms/AddressInput';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentPassenger } from '@/data/passenger.slice';
import { useClientMemberContact } from '@/hooks/useClientMemberContact';
import { usePortalLabel } from '@/hooks/usePortalLabel';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import {
  isWarningTripLegError,
  translateTripLegConfidenceError,
} from '../machines/tripLegConfidence';

export function TripArrayFieldAddressInput({
  index,
  tripField,
  label,
  required,
}: {
  tripField: 'pickupAddress' | 'dropoffAddress';
  index: number;
  label: string;
  required?: boolean;
}) {
  const { t } = useTranslation('common');
  const { watch, setValue, setError, clearErrors, formState } =
    useFormContext<ReservationFormInput>();
  const isClientPortal = usePortalLabel() === 'client-portal';
  const { mainSnapshot, fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);

  const name = `trips.${index}.${tripField}` as const;
  const place = useWatch({ name });

  useEffect(() => {
    actor.send({
      type: 'CHANGE',
      index,
      value: place ?? '',
    });
  }, [actor, index, place]);

  const passenger = useAppSelector(selectCurrentPassenger);
  const passengerAddresses = useMemo(
    () => passenger?.passengerAddresses ?? [],
    [passenger],
  );
  // {{clientName}}/{{clientNumber}} interpolation for the confidence message.
  // The contact is plan-scoped, so the passenger's service plan rides along.
  const clientMemberContact = useClientMemberContact(
    passenger?.clientBC,
    passenger?.servicePlans?.[0]?.servicePlanUUID,
  );

  const structFieldError =
    formState.errors.trips?.[index]?.[tripField]?.message;

  const addressValidationEnabled =
    mainSnapshot.context.featureFlags?.addressValidationEnabled ?? false;

  // Client portal shows low address-confidence as a non-blocking warning under
  // the field (other portals get a blocking error via the applier). The target
  // field was resolved at score time, so show the one that lands on THIS field.
  const confidenceWarningMessage = useMemo(() => {
    if (!isClientPortal || !addressValidationEnabled) return undefined;

    const warning = Object.values(mainSnapshot.context.errorsByTripIndex ?? {})
      .flat()
      .find(
        (err) =>
          err.formFieldPath === name &&
          isWarningTripLegError(err, isClientPortal),
      );
    if (!warning) return undefined;
    return (
      translateTripLegConfidenceError(t, warning, clientMemberContact) ||
      undefined
    );
  }, [
    isClientPortal,
    addressValidationEnabled,
    clientMemberContact,
    mainSnapshot,
    name,
    t,
  ]);

  return (
    <AddressInput
      name={name}
      label={label}
      required={required}
      errorMessage={structFieldError ? String(structFieldError) : ''}
      warningMessage={confidenceWarningMessage}
      watch={watch}
      setValue={setValue}
      errorHandlers={{ setError, clearErrors }}
      passengerAddresses={passengerAddresses}
    />
  );
}
